import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import CloseIcon from '@mui/icons-material/Close';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip as RechartsTooltip,
} from 'recharts';
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../hooks/useFetch';
import { useGetLegalTransactionTypes } from '../hooks/reactQuerys/LegalTransactionQueries';
import { useGetUsers } from '../hooks/reactQuerys/UserQueries';
import { useCourts } from '../Aemter/hooks';

type Operator = 'gt' | 'gte' | 'lt' | 'lte' | 'eq';
type Aggregate = 'count' | 'sum' | 'avg' | 'min' | 'max';
type FilterType = 'date' | 'int' | 'flag' | 'string';

interface IFilter {
  key: string;
  value?: string | number | boolean;
}
interface ISourceField {
  id: string;
  label: string;
}
interface ISourceFilterField {
  id: string;
  label: string;
  type: FilterType;
}
interface ISource {
  id: string;
  label: string;
  groupable: ISourceField[];
  filterable: ISourceFilterField[];
  aggregateFields: ISourceField[];
}
interface ISourcesResponse {
  sources: ISource[];
  aggregates: Aggregate[];
}
interface IKpiAlert {
  idKpiAlert: number;
  Title: string;
  Source?: string | null;
  Aggregate?: Aggregate | null;
  AggregateField?: string | null;
  Metric?: string | null;
  Operator: Operator;
  Threshold: number;
  Filters: IFilter[];
  IsActive: boolean;
  CooldownHours: number;
  LastTriggeredAt?: string | null;
  LastEvaluatedAt?: string | null;
  LastValue?: number | null;
}
interface IEvalResult {
  idKpiAlert: number;
  Title?: string;
  value: number | null;
  threshold: number;
  operator: Operator;
  triggered: boolean;
  mailSent: boolean;
  error?: string;
}

const OPERATOR_LABEL: Record<Operator, string> = {
  gt: 'größer als',
  gte: 'größer oder gleich',
  lt: 'kleiner als',
  lte: 'kleiner oder gleich',
  eq: 'gleich',
};

const AGG_LABEL: Record<Aggregate, string> = {
  count: 'Anzahl',
  sum: 'Summe',
  avg: 'Durchschnitt',
  min: 'Minimum',
  max: 'Maximum',
};

const emptyAlert = (): IKpiAlert => ({
  idKpiAlert: -1,
  Title: '',
  Source: '',
  Aggregate: 'count',
  AggregateField: null,
  Operator: 'gt',
  Threshold: 0,
  Filters: [],
  IsActive: true,
  CooldownHours: 24,
});

interface IHistoryItem {
  idKpiAlertLog: number;
  value: number | null;
  threshold: number | null;
  triggered: boolean;
  mailSent: boolean;
  at: string;
}

/**
 * Kompakte Sparkline für eine KPI-Karte. Zeigt die letzten N Auswertungen
 * + den Schwellenwert als Referenzlinie. Klein gehalten — keine Achsen,
 * keine Legende, nur die Trendlinie.
 */
const KpiSparkline: React.FC<{ idKpiAlert: number; threshold: number }> = ({
  idKpiAlert,
  threshold,
}) => {
  const { data } = useQuery<{ items: IHistoryItem[] }>({
    refetchOnWindowFocus: false,
    queryKey: ['/kpialert/history', idKpiAlert],
    queryFn: () =>
      customFetcher({
        endpoint: `/kpialert/${idKpiAlert}/history?limit=30`,
      }),
  });
  if (!data?.items?.length) {
    return (
      <Box sx={{ height: 56, display: 'flex', alignItems: 'center' }}>
        <Typography variant='caption' color='text.secondary'>
          Noch keine Historie — wird beim ersten Lauf gefüllt.
        </Typography>
      </Box>
    );
  }
  const points = data.items.map(d => ({
    at: d.at,
    value: d.value ?? 0,
  }));
  return (
    <Box sx={{ height: 56 }}>
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart
          data={points}
          margin={{ top: 4, right: 4, bottom: 0, left: 0 }}
        >
          <ReferenceLine
            y={threshold}
            stroke='#f57c00'
            strokeDasharray='3 3'
            strokeWidth={1}
          />
          <Line
            type='monotone'
            dataKey='value'
            stroke='#1976d2'
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          <RechartsTooltip
            formatter={(v: number) => [v, 'Wert']}
            labelFormatter={(at: string) =>
              new Date(at).toLocaleString('de-DE')
            }
            contentStyle={{ fontSize: 11 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

const formatDateTime = (s?: string | null) => {
  if (!s) return '–';
  try {
    return new Date(s).toLocaleString('de-DE');
  } catch {
    return s;
  }
};

/**
 * KPI/Alert-Verwaltung mit Source-basiertem Builder — gleiche Engine wie der
 * Custom-Report-Builder. Ein KPI ist konzeptionell ein Custom-Report ohne
 * Gruppierung (gibt einen Wert), erweitert um Operator + Schwellenwert für
 * den Trigger und Mail-Versand.
 *
 * Legacy-Alerts (mit fest verdrahteter `Metric`-ID, ohne Source) werden in
 * der Karten-Liste weiterhin korrekt angezeigt; beim Bearbeiten kann der
 * User auf eine Source umstellen.
 */
export const KpiAlertsView: React.FC = () => {
  const queryClient = useQueryClient();
  const { legalTransactionTypes } = useGetLegalTransactionTypes();
  const { users } = useGetUsers();
  const { courts } = useCourts();

  const { data: sourcesData } = useQuery<ISourcesResponse>({
    refetchOnWindowFocus: false,
    queryKey: ['/customreport/sources'],
    queryFn: () => customFetcher({ endpoint: '/customreport/sources' }),
  });

  // Liste der eigenen CustomReports — für „Aus eigenem Report übernehmen"
  const { data: customReports } = useQuery<
    Array<{
      idCustomReport: number;
      Title: string;
      Source: string;
      Aggregate: Aggregate;
      AggregateField?: string | null;
      Filters: IFilter[];
    }>
  >({
    refetchOnWindowFocus: false,
    queryKey: ['/customreport'],
    queryFn: () => customFetcher({ endpoint: '/customreport' }),
  });

  const { data: alerts, isLoading: isLoadingAlerts } = useQuery<IKpiAlert[]>({
    refetchOnWindowFocus: false,
    queryKey: ['/kpialert'],
    queryFn: () => customFetcher({ endpoint: '/kpialert' }),
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['/kpialert'] });

  const { mutateAsync: createAlert, isPending: isCreating } = useMutation({
    mutationFn: (input: Partial<IKpiAlert>) =>
      customFetcher({
        endpoint: '/kpialert',
        method: 'POST',
        body: input,
      }),
    onSuccess: invalidate,
  });
  const { mutateAsync: updateAlert, isPending: isUpdating } = useMutation({
    mutationFn: (input: { id: number; data: Partial<IKpiAlert> }) =>
      customFetcher({
        endpoint: `/kpialert/${input.id}`,
        method: 'PUT',
        body: input.data,
      }),
    onSuccess: invalidate,
  });
  const { mutateAsync: deleteAlert } = useMutation({
    mutationFn: (id: number) =>
      customFetcher({ endpoint: `/kpialert/${id}`, method: 'DELETE' }),
    onSuccess: invalidate,
  });
  const { mutateAsync: evaluate } = useMutation({
    mutationFn: (input: { id: number; sendMail: boolean }) =>
      customFetcher({
        endpoint: `/kpialert/${input.id}/evaluate?sendMail=${input.sendMail}`,
        method: 'POST',
      }) as Promise<IEvalResult>,
    onSuccess: invalidate,
  });
  const { mutateAsync: previewAdHoc } = useMutation({
    mutationFn: (cfg: {
      Source: string;
      Aggregate: Aggregate;
      AggregateField?: string | null;
      Filters: IFilter[];
    }) =>
      customFetcher({
        endpoint: '/customreport/preview',
        method: 'POST',
        body: { ...cfg, GroupBy: null, OrderBy: 'value-desc', LimitRows: 1 },
      }) as Promise<{ items: Array<{ label: string; value: number }> }>,
  });

  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<IKpiAlert>(emptyAlert());
  const [evalResult, setEvalResult] = useState<IEvalResult | null>(null);
  const [evalDialogOpen, setEvalDialogOpen] = useState(false);

  const [previewValue, setPreviewValue] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const selectedSource = useMemo(
    () => sourcesData?.sources.find(s => s.id === draft.Source) ?? null,
    [sourcesData, draft.Source]
  );

  const openCreate = () => {
    setDraft(emptyAlert());
    setPreviewValue(null);
    setPreviewError(null);
    setEditorOpen(true);
  };
  const openEdit = (a: IKpiAlert) => {
    setDraft({
      ...emptyAlert(),
      ...a,
      Source: a.Source ?? '',
      Aggregate: a.Aggregate ?? 'count',
    });
    setPreviewValue(null);
    setPreviewError(null);
    setEditorOpen(true);
  };

  const remove = async (a: IKpiAlert) => {
    if (!window.confirm(`Alert „${a.Title}" wirklich löschen?`)) return;
    await deleteAlert(a.idKpiAlert);
  };

  const runEval = async (a: IKpiAlert, sendMail: boolean) => {
    const res = await evaluate({ id: a.idKpiAlert, sendMail });
    setEvalResult(res);
    setEvalDialogOpen(true);
  };

  const handleSourceChange = (sourceId: string) => {
    setDraft({
      ...draft,
      Source: sourceId,
      Aggregate: 'count',
      AggregateField: null,
      Filters: [],
    });
  };

  const addFilter = () => {
    if (!selectedSource?.filterable.length) return;
    setDraft({
      ...draft,
      Filters: [...draft.Filters, { key: selectedSource.filterable[0].id }],
    });
  };
  const updateFilterAt = (idx: number, patch: Partial<IFilter>) => {
    const next = [...draft.Filters];
    next[idx] = { ...next[idx], ...patch };
    setDraft({ ...draft, Filters: next });
  };
  const removeFilterAt = (idx: number) => {
    const next = [...draft.Filters];
    next.splice(idx, 1);
    setDraft({ ...draft, Filters: next });
  };

  // Live-Vorschau des Aggregat-Werts (debounced)
  useEffect(() => {
    if (!editorOpen || !draft.Source) {
      setPreviewValue(null);
      return;
    }
    const timer = setTimeout(() => {
      setPreviewLoading(true);
      setPreviewError(null);
      previewAdHoc({
        Source: draft.Source!,
        Aggregate: draft.Aggregate || 'count',
        AggregateField: draft.AggregateField,
        Filters: draft.Filters,
      })
        .then(r => {
          // Ohne GroupBy gibt's einen einzigen Wert mit label='Gesamt'.
          setPreviewValue(r.items?.[0]?.value ?? 0);
        })
        .catch(e =>
          setPreviewError(
            e instanceof Error ? e.message : 'Vorschau fehlgeschlagen.'
          )
        )
        .finally(() => setPreviewLoading(false));
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [editorOpen, JSON.stringify(draft)]);

  const submit = async () => {
    if (!draft.Title.trim()) {
      window.alert('Bitte einen Titel eingeben.');
      return;
    }
    if (!draft.Source) {
      window.alert('Bitte eine Datenquelle wählen.');
      return;
    }
    const payload: Partial<IKpiAlert> = {
      Title: draft.Title.trim(),
      Source: draft.Source,
      Aggregate: draft.Aggregate || 'count',
      AggregateField:
        draft.Aggregate && draft.Aggregate !== 'count'
          ? draft.AggregateField || null
          : null,
      Operator: draft.Operator,
      Threshold: Number(draft.Threshold),
      Filters: draft.Filters,
      IsActive: draft.IsActive,
      CooldownHours: Number(draft.CooldownHours) || 24,
    };
    if (draft.idKpiAlert > 0) {
      await updateAlert({ id: draft.idKpiAlert, data: payload });
    } else {
      await createAlert(payload);
    }
    setEditorOpen(false);
  };

  const renderFilterEditor = (f: IFilter, idx: number) => {
    if (!selectedSource) return null;
    const field = selectedSource.filterable.find(x => x.id === f.key);
    if (!field) return null;

    const valueEditor = (() => {
      if (field.type === 'flag') {
        return (
          <FormControlLabel
            label='Aktiv'
            control={
              <Switch
                size='small'
                checked={!!f.value}
                onChange={e => updateFilterAt(idx, { value: e.target.checked })}
              />
            }
          />
        );
      }
      if (field.type === 'date') {
        return (
          <TextField
            label='Wert'
            type='date'
            size='small'
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={(f.value as string) ?? ''}
            onChange={e => updateFilterAt(idx, { value: e.target.value })}
          />
        );
      }
      if (field.type === 'int') {
        if (f.key === 'idLegalTransactionType') {
          return (
            <Autocomplete
              size='small'
              options={legalTransactionTypes || []}
              getOptionLabel={t => t.LegalTransactionType}
              isOptionEqualToValue={(o, v) =>
                o.idLegalTransactionType === v.idLegalTransactionType
              }
              value={
                legalTransactionTypes?.find(
                  t => t.idLegalTransactionType === f.value
                ) ?? null
              }
              onChange={(_, v) =>
                updateFilterAt(idx, {
                  value: v ? v.idLegalTransactionType : undefined,
                })
              }
              renderInput={params => (
                <TextField {...params} label='Rechtsgeschäftsart' />
              )}
            />
          );
        }
        if (
          f.key === 'idEmployee' ||
          f.key === 'idAssignee' ||
          f.key === 'idNotary'
        ) {
          return (
            <Autocomplete
              size='small'
              options={users || []}
              getOptionLabel={u => `${u.FirstName} ${u.LastName}`}
              isOptionEqualToValue={(o, v) => o.idUser === v.idUser}
              value={users?.find(u => u.idUser === f.value) ?? null}
              onChange={(_, v) =>
                updateFilterAt(idx, { value: v ? v.idUser : undefined })
              }
              renderInput={params => (
                <TextField {...params} label='Mitarbeiter' />
              )}
            />
          );
        }
        if (f.key === 'idCourt') {
          return (
            <Autocomplete
              size='small'
              options={courts || []}
              getOptionLabel={c => c.Court}
              isOptionEqualToValue={(o, v) => o.idCourt === v.idCourt}
              value={courts?.find(c => c.idCourt === f.value) ?? null}
              onChange={(_, v) =>
                updateFilterAt(idx, { value: v ? v.idCourt : undefined })
              }
              renderInput={params => <TextField {...params} label='Gericht' />}
            />
          );
        }
        return (
          <TextField
            label='Wert'
            type='number'
            size='small'
            fullWidth
            value={(f.value as number | undefined) ?? ''}
            onChange={e =>
              updateFilterAt(idx, {
                value:
                  e.target.value === '' ? undefined : Number(e.target.value),
              })
            }
          />
        );
      }
      return (
        <TextField
          label='Wert'
          size='small'
          fullWidth
          value={(f.value as string) ?? ''}
          onChange={e => updateFilterAt(idx, { value: e.target.value })}
        />
      );
    })();

    return (
      <Stack
        key={idx}
        direction='row'
        spacing={1}
        alignItems='center'
        sx={{ mt: 1 }}
      >
        <TextField
          label='Filter'
          select
          size='small'
          value={f.key}
          onChange={e =>
            updateFilterAt(idx, { key: e.target.value, value: undefined })
          }
          sx={{ minWidth: 220 }}
        >
          {selectedSource.filterable.map(ff => (
            <MenuItem key={ff.id} value={ff.id}>
              {ff.label}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }}>{valueEditor}</Box>
        <IconButton size='small' onClick={() => removeFilterAt(idx)}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Stack>
    );
  };

  return (
    <Box>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant='h6'>KPIs &amp; Alerts</Typography>
          <Typography variant='caption' color='text.secondary'>
            Definieren Sie Kennzahlen mit Schwellenwerten — gleiche Datenquellen
            wie im Report-Builder. Wird ein Alert ausgelöst, erhalten Sie
            automatisch eine E-Mail.
          </Typography>
        </Box>
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Neuer Alert
        </Button>
      </Stack>

      {isLoadingAlerts ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !alerts?.length ? (
        <Alert severity='info'>
          Sie haben noch keine Alerts angelegt. Klicken Sie auf „Neuer Alert".
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {alerts.map(a => {
            const source = sourcesData?.sources.find(s => s.id === a.Source);
            const subtitle = source
              ? `${source.label} · ${AGG_LABEL[a.Aggregate || 'count']}${a.AggregateField ? ' ' + a.AggregateField : ''}`
              : a.Metric || '–';
            return (
              <Grid item xs={12} md={6} key={a.idKpiAlert}>
                <Card variant='outlined'>
                  <CardContent>
                    <Stack
                      direction='row'
                      justifyContent='space-between'
                      alignItems='flex-start'
                    >
                      <Box sx={{ flex: 1 }}>
                        <Stack
                          direction='row'
                          spacing={1}
                          alignItems='center'
                          sx={{ mb: 0.5 }}
                        >
                          {a.IsActive ? (
                            <NotificationsActiveIcon
                              fontSize='small'
                              color='primary'
                            />
                          ) : (
                            <NotificationsOffIcon
                              fontSize='small'
                              color='disabled'
                            />
                          )}
                          <Typography variant='subtitle1' fontWeight={600}>
                            {a.Title}
                          </Typography>
                        </Stack>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ display: 'block' }}
                        >
                          {subtitle}
                        </Typography>
                        <Typography
                          variant='caption'
                          color='text.secondary'
                          sx={{ display: 'block' }}
                        >
                          Alarm wenn Wert {OPERATOR_LABEL[a.Operator]}{' '}
                          <strong>{a.Threshold}</strong>
                        </Typography>
                      </Box>
                      <Stack direction='row'>
                        <Tooltip title='Jetzt prüfen (ohne Mail)'>
                          <IconButton
                            size='small'
                            onClick={() => runEval(a, false)}
                          >
                            <PlayArrowIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Test-Mail senden, falls Bedingung erfüllt'>
                          <IconButton
                            size='small'
                            onClick={() => runEval(a, true)}
                          >
                            <EmailIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Bearbeiten'>
                          <IconButton size='small' onClick={() => openEdit(a)}>
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Löschen'>
                          <IconButton size='small' onClick={() => remove(a)}>
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    <Divider sx={{ my: 1.5 }} />

                    <Stack
                      direction='row'
                      spacing={2}
                      flexWrap='wrap'
                      useFlexGap
                    >
                      <Box>
                        <Typography variant='caption' color='text.secondary'>
                          Letzter Wert
                        </Typography>
                        <Typography variant='body2' fontWeight={600}>
                          {a.LastValue != null ? a.LastValue : '–'}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant='caption' color='text.secondary'>
                          Zuletzt geprüft
                        </Typography>
                        <Typography variant='body2'>
                          {formatDateTime(a.LastEvaluatedAt)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant='caption' color='text.secondary'>
                          Zuletzt ausgelöst
                        </Typography>
                        <Typography variant='body2'>
                          {formatDateTime(a.LastTriggeredAt)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant='caption' color='text.secondary'>
                          Cooldown
                        </Typography>
                        <Typography variant='body2'>
                          {a.CooldownHours} h
                        </Typography>
                      </Box>
                    </Stack>

                    <Box sx={{ mt: 1.5 }}>
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ display: 'block', mb: 0.5 }}
                      >
                        Trend (letzte 30 Auswertungen) — Schwellenwert als
                        gestrichelte Linie
                      </Typography>
                      <KpiSparkline
                        idKpiAlert={a.idKpiAlert}
                        threshold={a.Threshold}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ----------------- Editor ----------------- */}
      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>
          {draft.idKpiAlert > 0 ? 'Alert bearbeiten' : 'Neuer Alert'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <TextField
                  label='Titel'
                  size='small'
                  fullWidth
                  value={draft.Title}
                  onChange={e => setDraft({ ...draft, Title: e.target.value })}
                  placeholder='z. B. „Wenig Logins der letzten 7 Tage"'
                />

                {/* Schnell-Auswahl: gespeicherten Report als Vorlage übernehmen */}
                {customReports && customReports.length > 0 && (
                  <TextField
                    label='Aus eigenem Report übernehmen'
                    select
                    size='small'
                    fullWidth
                    value=''
                    helperText='Quelle, Aggregat und Filter werden vom Report übernommen — Operator und Schwellenwert legen Sie hier fest.'
                    onChange={e => {
                      const idCR = Number(e.target.value);
                      const cr = customReports.find(
                        r => r.idCustomReport === idCR
                      );
                      if (!cr) return;
                      setDraft({
                        ...draft,
                        Source: cr.Source,
                        Aggregate: cr.Aggregate,
                        AggregateField: cr.AggregateField ?? null,
                        Filters: cr.Filters || [],
                      });
                    }}
                  >
                    <MenuItem value=''>– oder unten manuell wählen –</MenuItem>
                    {customReports.map(r => (
                      <MenuItem key={r.idCustomReport} value={r.idCustomReport}>
                        {r.Title}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <TextField
                  label='Datenquelle'
                  select
                  size='small'
                  fullWidth
                  value={draft.Source ?? ''}
                  onChange={e => handleSourceChange(e.target.value)}
                >
                  <MenuItem value=''>– Bitte wählen –</MenuItem>
                  {(sourcesData?.sources || []).map(s => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.label}
                    </MenuItem>
                  ))}
                </TextField>

                {selectedSource && (
                  <>
                    <Stack direction='row' spacing={1}>
                      <TextField
                        label='Aggregat'
                        select
                        size='small'
                        sx={{ minWidth: 160 }}
                        value={draft.Aggregate ?? 'count'}
                        onChange={e =>
                          setDraft({
                            ...draft,
                            Aggregate: e.target.value as Aggregate,
                            AggregateField:
                              e.target.value === 'count'
                                ? null
                                : draft.AggregateField ||
                                  selectedSource.aggregateFields[0]?.id ||
                                  null,
                          })
                        }
                      >
                        {(sourcesData?.aggregates || []).map(a => (
                          <MenuItem key={a} value={a}>
                            {AGG_LABEL[a]}
                          </MenuItem>
                        ))}
                      </TextField>
                      {draft.Aggregate !== 'count' && (
                        <TextField
                          label='Feld'
                          select
                          size='small'
                          fullWidth
                          value={draft.AggregateField ?? ''}
                          onChange={e =>
                            setDraft({
                              ...draft,
                              AggregateField: e.target.value || null,
                            })
                          }
                          disabled={!selectedSource.aggregateFields.length}
                          helperText={
                            !selectedSource.aggregateFields.length
                              ? 'Keine numerischen Felder — nur „Anzahl" möglich.'
                              : undefined
                          }
                        >
                          {selectedSource.aggregateFields.map(f => (
                            <MenuItem key={f.id} value={f.id}>
                              {f.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    </Stack>

                    <Box>
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='space-between'
                      >
                        <Typography variant='subtitle2'>Filter</Typography>
                        <Button
                          size='small'
                          startIcon={<AddIcon fontSize='small' />}
                          onClick={addFilter}
                          disabled={!selectedSource.filterable.length}
                        >
                          Filter hinzufügen
                        </Button>
                      </Stack>
                      {draft.Filters.map((f, idx) =>
                        renderFilterEditor(f, idx)
                      )}
                    </Box>
                  </>
                )}

                <Divider />

                <Typography variant='subtitle2'>Alarm-Bedingung</Typography>
                <Stack direction='row' spacing={1}>
                  <TextField
                    label='Operator'
                    select
                    size='small'
                    sx={{ minWidth: 200 }}
                    value={draft.Operator}
                    onChange={e =>
                      setDraft({
                        ...draft,
                        Operator: e.target.value as Operator,
                      })
                    }
                  >
                    {(['gt', 'gte', 'lt', 'lte', 'eq'] as Operator[]).map(o => (
                      <MenuItem key={o} value={o}>
                        Wert {OPERATOR_LABEL[o]}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label='Schwellenwert'
                    type='number'
                    size='small'
                    fullWidth
                    value={draft.Threshold}
                    onChange={e =>
                      setDraft({ ...draft, Threshold: Number(e.target.value) })
                    }
                  />
                </Stack>

                <Stack direction='row' spacing={2} alignItems='center'>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={draft.IsActive}
                        onChange={e =>
                          setDraft({ ...draft, IsActive: e.target.checked })
                        }
                      />
                    }
                    label='Aktiv'
                  />
                  <TextField
                    label='Cooldown'
                    type='number'
                    size='small'
                    sx={{ width: 130 }}
                    value={draft.CooldownHours}
                    onChange={e =>
                      setDraft({
                        ...draft,
                        CooldownHours: Number(e.target.value) || 24,
                      })
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position='end'>h</InputAdornment>
                      ),
                    }}
                  />
                </Stack>
              </Stack>
            </Grid>

            {/* Live-Vorschau */}
            <Grid item xs={12} md={5}>
              <Paper variant='outlined' sx={{ p: 2 }}>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ display: 'block', mb: 1 }}
                >
                  Aktueller Wert
                </Typography>
                {!draft.Source ? (
                  <Alert severity='info'>
                    Datenquelle wählen, um den Wert zu berechnen.
                  </Alert>
                ) : previewError ? (
                  <Alert severity='error'>{previewError}</Alert>
                ) : previewLoading ? (
                  <Box
                    sx={{ display: 'flex', justifyContent: 'center', py: 3 }}
                  >
                    <CircularProgress size={28} />
                  </Box>
                ) : previewValue != null ? (
                  <>
                    <Typography variant='h3' fontWeight={600}>
                      {previewValue}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        size='small'
                        label={(() => {
                          const op = draft.Operator;
                          const cmp = (a: number, b: number) =>
                            op === 'gt'
                              ? a > b
                              : op === 'gte'
                                ? a >= b
                                : op === 'lt'
                                  ? a < b
                                  : op === 'lte'
                                    ? a <= b
                                    : a === b;
                          return cmp(previewValue, draft.Threshold)
                            ? `Würde ausgelöst (${previewValue} ${OPERATOR_LABEL[op]} ${draft.Threshold})`
                            : `Nicht ausgelöst (${previewValue} ist nicht ${OPERATOR_LABEL[op]} ${draft.Threshold})`;
                        })()}
                        color={(() => {
                          const op = draft.Operator;
                          const cmp = (a: number, b: number) =>
                            op === 'gt'
                              ? a > b
                              : op === 'gte'
                                ? a >= b
                                : op === 'lt'
                                  ? a < b
                                  : op === 'lte'
                                    ? a <= b
                                    : a === b;
                          return cmp(previewValue, draft.Threshold)
                            ? 'warning'
                            : 'default';
                        })()}
                      />
                    </Box>
                  </>
                ) : null}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Abbrechen</Button>
          <Button
            variant='contained'
            disabled={isCreating || isUpdating}
            onClick={submit}
          >
            {isCreating || isUpdating ? 'Speichere…' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auswertungs-Ergebnis */}
      <Dialog
        open={evalDialogOpen}
        onClose={() => setEvalDialogOpen(false)}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle>Auswertungs-Ergebnis</DialogTitle>
        <DialogContent dividers>
          {evalResult?.error ? (
            <Alert severity='error'>{evalResult.error}</Alert>
          ) : evalResult ? (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Aktueller Wert
                </Typography>
                <Typography variant='h5'>
                  {evalResult.value != null ? evalResult.value : '–'}
                </Typography>
              </Box>
              <Box>
                <Typography variant='caption' color='text.secondary'>
                  Bedingung
                </Typography>
                <Typography>
                  Wert {OPERATOR_LABEL[evalResult.operator]}{' '}
                  <strong>{evalResult.threshold}</strong>
                </Typography>
              </Box>
              <Stack direction='row' spacing={1}>
                <Chip
                  size='small'
                  label={evalResult.triggered ? 'Ausgelöst' : 'Nicht ausgelöst'}
                  color={evalResult.triggered ? 'warning' : 'default'}
                />
                {evalResult.mailSent && (
                  <Chip
                    size='small'
                    label='Mail versendet'
                    color='success'
                    icon={<EmailIcon fontSize='small' />}
                  />
                )}
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEvalDialogOpen(false)}>Schließen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
